import { useState, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addRack, updateRack, getWarehouseList } from 'helpers/backend_helper';
import { Common } from 'helpers/common';

export const useRackModal = (onSave: (result: any) => void, initialData?: any) => {
    const [show, setShow] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [rackData, setRackData] = useState<any>(initialData);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [warehouseList, setWarehouseList] = useState<any[]>([]);
    const commonHelper = new Common();

    useEffect(() => {
        if (initialData) {
            setRackData(initialData);
            setIsEdit(!!initialData.id);
        }
    }, [initialData]);

    useEffect(() => {
        loadWarehouses();
    }, []);

    const loadWarehouses = async () => {
        try {
            const response = await getWarehouseList();
            if (response) {
                setWarehouseList(response.data || []);
            }
        } catch (error) {
            console.error('Error loading warehouses:', error);
        }
    };

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setRackData(null);
        }
    }, [show]);

    const handleEditDataClick = (ele: any) => {
        setRackData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddRack = () => {
        setRackData({});
        toggle();
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: rackData?.name || "",
            warehouse_id: rackData?.warehouse_id || ""
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Please enter rack name"),
            warehouse_id: Yup.string().required("Please select warehouse")
        }),
        onSubmit: async (values: any) => {
            setIsSaving(true);
            try {
                let result;
                if (isEdit) {
                    result = await updateRack({ ...values, id: rackData.id });
                    commonHelper.showSuccessMessage("Rack updated successfully");
                } else {
                    result = await addRack(values);
                    commonHelper.showSuccessMessage("Rack added successfully");
                }
                
                if (result) {
                    onSave(result);
                }
            } catch (error) {
                console.error('Error saving rack:', error);
                commonHelper.showErrorMessage(isEdit ? "Failed to update rack" : "Failed to add rack");
            } finally {
                setIsSaving(false);
            }
        }
    });

    return {
        show,
        isEdit,
        rackData,
        toggle,
        handleEditDataClick,
        handleAddRack,
        validation,
        isSaving,
        warehouseList
    };
};